import { useHoverPopover } from '@app/core/hooks';
import { ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { NavLink, NavLinkProps } from 'react-router-dom';
import { useSidenavContext } from '../SidenavContext';
import { getSidenavRailPaddingLeft, SIDENAV_TOKENS } from '../sidenav-tokens';
import { PopoverTitle } from './PopoverTitle';
import { SidenavPopover } from './SidenavPopover';
import {
  getCommonNavButtonStyles,
  getNavItemIconStyles,
  getNavItemLayoutStyles,
  getNavItemTextStyles,
} from './nav-item-styles';

export type NavItemProps = Omit<NavLinkProps, 'component' | 'className' | 'style'> & {
  icon?: React.ReactNode;
  /** When true, active styling keeps parent (accent ribbon) typography instead of leaf white/medium. */
  hasChildren?: boolean;
};

/** Top-level sidenav link with optional collapsed hover label. */
export function NavItem({
  icon,
  children,
  hasChildren = false,
  onClick,
  ...navLinkProps
}: NavItemProps) {
  const { collapsed } = useSidenavContext();
  const tokens = SIDENAV_TOKENS;
  // Parents with children own their collapsed popover (NavItemWithChildren).
  const {
    anchorEl,
    menuOpen,
    handleMouseEnter,
    handleMouseLeave,
    handlePopoverMouseEnter,
    handlePopoverMouseLeave,
    closePopover,
  } = useHoverPopover({ enabled: collapsed && !hasChildren });

  /** Closes the collapsed hover label on navigate so it does not linger after click. */
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (collapsed) {
      closePopover();
    }
    onClick?.(event);
  };

  // Same left inset in both modes — centers in the collapsed rail and never shifts on expand.
  const iconPaddingLeft = getSidenavRailPaddingLeft(tokens.iconSize);

  return (
    <>
      <ListItem disablePadding sx={{ display: 'block' }}>
        <ListItemButton
          component={NavLink}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
          disableRipple
          sx={[
            (theme) => getCommonNavButtonStyles(theme, { hasChildren, collapsed }),
            getNavItemLayoutStyles(iconPaddingLeft),
          ]}
          {...navLinkProps}
        >
          {icon && <ListItemIcon sx={(theme) => getNavItemIconStyles(theme)}>{icon}</ListItemIcon>}
          <ListItemText primary={children} sx={getNavItemTextStyles(collapsed)} />
        </ListItemButton>
      </ListItem>
      {collapsed && !hasChildren && (
        <SidenavPopover
          open={menuOpen}
          anchorEl={anchorEl}
          onClose={closePopover}
          onMouseEnter={handlePopoverMouseEnter}
          onMouseLeave={handlePopoverMouseLeave}
        >
          <PopoverTitle label={children as string} to={navLinkProps.to} onClick={closePopover} />
        </SidenavPopover>
      )}
    </>
  );
}
